<template>
  <transition name="slide-fade">
    <div class="auth-container">
      <div class="auth-panel">
        <h2 class="auth-title">
          {{ showResetForm ? "Reset Password" : "Login" }}
        </h2>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message" role="alert">
          {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="success-message" role="alert">
          {{ successMessage }}
        </div>

        <!-- Login Form -->
        <form
          v-if="!showResetForm"
          @submit.prevent="handleLogin"
          class="auth-form"
        >
          <!-- Email Field -->
          <div class="form-group">
            <label for="email">Email Address</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              placeholder="your.email@example.com"
            />
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              autocomplete="current-password"
              placeholder="Enter your password"
            />
          </div>

          <!-- Submit Button -->
          <button type="submit" class="btn-primary" :disabled="loading">
            <span v-if="!loading">Login</span>
            <span v-else>Logging in...</span>
          </button>

          <!-- Divider -->
          <div class="divider">
            <span>Or continue with</span>
          </div>

          <!-- Google Sign In Button -->
          <button
            type="button"
            @click="handleGoogleLogin"
            :disabled="loading"
            class="btn-google"
          >
            <svg
              class="google-icon"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <span>Google</span>
          </button>
        </form>

        <!-- Password Reset Form -->
        <form
          v-if="showResetForm"
          @submit.prevent="handleReset"
          class="auth-form"
        >
          <!-- Email Field -->
          <div class="form-group">
            <label for="reset-email">Email Address</label>
            <input
              id="reset-email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              placeholder="your.email@example.com"
            />
            <p class="help-text">
              We'll send you a link to reset your password.
            </p>
          </div>

          <!-- Submit Button -->
          <button type="submit" class="btn-primary" :disabled="loading">
            <span v-if="!loading">Send Reset Link</span>
            <span v-else>Sending...</span>
          </button>

          <!-- Back to Login -->
          <div class="auth-footer">
            <button
              type="button"
              @click="
                showResetForm = false;
                errorMessage = null;
                successMessage = null;
              "
              class="link-button"
            >
              Back to Login
            </button>
          </div>
        </form>

        <!-- Forgot Password Link (only shown in login form) -->
        <div v-if="!showResetForm" class="auth-footer">
          <button
            type="button"
            @click="
              showResetForm = true;
              errorMessage = null;
            "
            class="link-button"
          >
            Forgot Password?
          </button>
        </div>

        <!-- Privacy Notice -->
        <p class="disclaimer">
          This symptom and behavior tracker is designed solely for your personal
          use. Any data that you choose to enter will not be accessed by us or
          shared with third parties.
        </p>
      </div>
    </div>
  </transition>
</template>

<script>
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import { mapMutations } from "vuex";
import { trackLogin, trackSessionStart } from "@/utils/analytics";

export default {
  name: "auth",
  data() {
    return {
      showResetForm: false,
      email: "",
      password: "",
      loading: false,
      errorMessage: null,
      successMessage: null,
    };
  },
  created() {
    firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        console.log("[Auth.vue] User authenticated:", user.uid);

        // Track successful login and session start
        const providerId =
          user.providerData &&
          user.providerData[0] &&
          user.providerData[0].providerId
            ? user.providerData[0].providerId
            : "password";
        await trackLogin(user.uid, providerId);
        await trackSessionStart(user.uid);

        // Check if there's a return URL from the static site
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = urlParams.get("returnUrl");

        console.log("[Auth.vue] Return URL:", returnUrl);

        if (returnUrl) {
          // Redirect back to the static site page they came from
          const decodedUrl = decodeURIComponent(returnUrl);
          console.log("[Auth.vue] Redirecting to:", decodedUrl);
          window.location.href = decodedUrl;
        } else {
          // Default: go to app dashboard
          console.log("[Auth.vue] No return URL, going to dashboard");
          this.$router.push("/");
        }
      }
    });
  },
  methods: {
    ...mapMutations([
      "changePageTitle",
      "changePageIntroText",
      "modifyLoggedIn",
    ]),

    async handleLogin() {
      this.errorMessage = null;
      this.successMessage = null;
      this.loading = true;

      try {
        console.log("[Auth.vue] Logging in...");
        await firebase
          .auth()
          .signInWithEmailAndPassword(this.email, this.password);
        // Auth state change will handle redirect
      } catch (error) {
        console.error("[Auth.vue] Login error:", error);
        this.errorMessage = this.getErrorMessage(error.code);
      } finally {
        this.loading = false;
      }
    },

    async handleGoogleLogin() {
      this.errorMessage = null;
      this.successMessage = null;
      this.loading = true;

      try {
        console.log("[Auth.vue] Logging in with Google...");
        const provider = new firebase.auth.GoogleAuthProvider();
        await firebase.auth().signInWithPopup(provider);
        // Auth state change will handle redirect
      } catch (error) {
        console.error("[Auth.vue] Google login error:", error);
        // Handle popup closed by user
        if (error.code === "auth/popup-closed-by-user") {
          this.errorMessage = null; // Don't show error if user simply closed the popup
        } else if (error.code === "auth/cancelled-popup-request") {
          this.errorMessage = null; // Don't show error if popup was cancelled
        } else {
          this.errorMessage = this.getErrorMessage(error.code);
        }
      } finally {
        this.loading = false;
      }
    },

    async handleReset() {
      // Reset messages
      this.errorMessage = null;
      this.successMessage = null;

      // Validate email
      if (!this.email) {
        this.errorMessage = "Please enter your email address.";
        return;
      }

      this.loading = true;

      try {
        console.log("[Auth.vue] Sending password reset email...");
        await firebase.auth().sendPasswordResetEmail(this.email);

        // Show success message
        this.successMessage =
          "Password reset email sent! Please check your inbox.";
        this.errorMessage = null;

        // Switch back to login form after 3 seconds
        setTimeout(() => {
          this.showResetForm = false;
          this.successMessage = null;
        }, 3000);
      } catch (error) {
        console.error("[Auth.vue] Password reset error:", error);
        this.errorMessage = this.getErrorMessage(error.code);
      } finally {
        this.loading = false;
      }
    },

    getErrorMessage(errorCode) {
      const messages = {
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/user-disabled": "This account has been disabled.",
        "auth/user-not-found": "No account found with this email address.",
        "auth/wrong-password": "Incorrect password. Please try again.",
        "auth/email-already-in-use":
          "An account with this email already exists.",
        "auth/weak-password": "Password should be at least 6 characters.",
        "auth/too-many-requests":
          "Too many failed attempts. Please try again later.",
        "auth/network-request-failed":
          "Network error. Please check your connection.",
        "auth/popup-blocked":
          "Please allow popups for this website to sign in with Google.",
        "auth/account-exists-with-different-credential":
          "An account already exists with the same email address but different sign-in credentials.",
        "auth/operation-not-allowed":
          "This sign-in method is not enabled. Please contact support.",
      };

      return messages[errorCode] || "An error occurred. Please try again.";
    },
  },
  mounted() {
    // Set logged in to false
    this.modifyLoggedIn(false);

    // Set the page title
    this.changePageTitle("Login");

    // Set page text
    this.changePageIntroText();
  },
};
</script>

<style scoped>
.auth-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
}

.auth-panel {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.auth-title {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #1a202c;
}

.disclaimer {
  line-height: 1.4;
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.75rem;
  color: #4a5568;
  padding: 0.75rem;
  background: #f7fafc;
  border-radius: 4px;
}

.error-message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #fed7d7;
  border: 1px solid #fc8181;
  color: #c53030;
  border-radius: 4px;
  font-size: 0.875rem;
}

.success-message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #c6f6d5;
  border: 1px solid #68d391;
  color: #22543d;
  border-radius: 4px;
  font-size: 0.875rem;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
}

.form-group input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #00274c;
  box-shadow: 0 0 0 3px rgba(0, 39, 76, 0.1);
}

.help-text {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

.btn-primary {
  width: 100%;
  background: #00274c;
  color: #ffcb05;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
}

.btn-primary:hover:not(:disabled) {
  background: #ffcb05;
  color: #00274c;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Divider */
.divider {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #e2e8f0;
}

.divider span {
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: #718096;
  background: white;
}

/* Google Button */
.btn-google {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-google:hover:not(:disabled) {
  background: #f7fafc;
}

.btn-google:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.google-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.auth-footer {
  margin-top: 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4a5568;
}

.link-button {
  background: none;
  border: none;
  color: #3182ce;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
}

.link-button:hover {
  text-decoration: underline;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
